// @ts-nocheck — interacts with untyped store and hooks
import {Star} from "@phosphor-icons/react";
import {useNavigate} from "react-router-dom";
import fallbackImage from "@/assets/images/fallback-image.svg";
import useAddFavoriteGame from "@/hooks/games/useAddFavoriteGame.hook";
import useDeleteFavoriteGame from "@/hooks/games/useDeleteFavoriteGame.hook";
import {useMiniGames} from "@/store/useMiniGames";

// Catalog-specific card — w-full + aspect-square so it fills the grid cell responsively
const cardCls =
  "surface-elev-2 group relative w-full aspect-square rounded-[var(--radius-lg)] overflow-hidden " +
  "cursor-pointer ring-1 ring-[var(--color-border-subtle)] shadow-[var(--shadow-md)] " +
  "transition-[transform,box-shadow,ring-color] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] " +
  "hover:scale-[1.04] hover:z-[3] hover:ring-[var(--color-primary-soft)] " +
  "hover:shadow-[0_12px_32px_rgba(0,0,0,0.6),0_0_0_1px_var(--color-primary-soft)]";

const imageCls = "w-full h-full object-cover block";

const overlayCls =
  "absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0.35)_0%,rgba(0,0,0,0.82)_100%)] " +
  "flex flex-col items-center justify-center gap-1.5 opacity-0 transition-opacity duration-200 z-[3] group-hover:opacity-100 px-2";

const favBtnCls =
  "absolute top-1.5 right-1.5 z-[4] p-1 rounded-full " +
  "bg-[rgba(0,0,0,0.45)] border-0 cursor-pointer " +
  "transition-opacity duration-200 opacity-100 group-hover:opacity-0";

// Proportional to smaller card — compact text + button
const nameCls =
  "font-[Oswald,sans-serif] font-bold text-[11px] min-[640px]:text-[12px] min-[960px]:text-[13px] " +
  "leading-tight text-white text-center w-full " +
  "[text-shadow:0_1px_6px_rgba(0,0,0,0.7)] line-clamp-2";

const playBtnCls =
  "h-7 min-[640px]:h-8 px-3 rounded-full font-[Open_Sans,sans-serif] font-bold " +
  "text-[10px] min-[640px]:text-[11px] text-[var(--color-text-inverse)] " +
  "bg-[var(--color-primary)] border-0 cursor-pointer flex items-center justify-center " +
  "transition-[filter,box-shadow,transform] duration-200 " +
  "hover:brightness-105 hover:shadow-[0_0_6px_2px_rgba(253,238,51,0.65),0_0_14px_4px_rgba(253,238,51,0.35)] " +
  "active:scale-[0.97] whitespace-nowrap";

interface CatalogCardProps {
  gameId: string;
  name: string;
  provider: string;
  icon: string;
  isFav: boolean;
}

const CatalogCard = ({gameId, name, provider, icon, isFav}: CatalogCardProps) => {
  const navigate = useNavigate();
  const setFavoriteGames = useMiniGames(state => state.setFavoriteGames);
  const {addFavoriteGame} = useAddFavoriteGame();
  const {deleteFavoriteGame} = useDeleteFavoriteGame();

  const handlePlay = () => {
    navigate(`/games/${encodeURIComponent(provider)}/${encodeURIComponent(gameId)}`);
  };

  const handleFav = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFavoriteGames({id: gameId, provider, game_provider: provider}, addFavoriteGame, deleteFavoriteGame);
  };

  return (
    <div
      className={cardCls}
      onClick={handlePlay}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handlePlay();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Jugar ${name}`}
    >
      {/* Game image */}
      <img
        src={icon || fallbackImage}
        alt={name}
        className={imageCls}
        onError={e => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = fallbackImage;
        }}
      />

      {/* Favorite star — top-right, hides on hover */}
      <button
        type="button"
        className={favBtnCls}
        onClick={handleFav}
        aria-label={isFav ? "Quitar de favoritos" : "Agregar a favoritos"}
      >
        <Star
          size={16}
          weight={isFav ? "fill" : "regular"}
          color={isFav ? "var(--color-primary)" : "rgba(255,255,255,0.8)"}
        />
      </button>

      {/* Hover overlay — name + play button with glow */}
      <div className={overlayCls}>
        <span className={nameCls}>{name}</span>
        <button
          type="button"
          className={playBtnCls}
          onClick={e => {
            e.stopPropagation();
            handlePlay();
          }}
          tabIndex={-1}
        >
          Jugar Ya
        </button>
      </div>
    </div>
  );
};

export default CatalogCard;
